import {ChangeEvent, MouseEvent, useEffect, useRef, useState} from 'react';
import {CircleQuestionMark} from "lucide-react";
import {ICON_SIZE} from "components/common/constants";
import {Tooltip} from "components/tooltip/Tooltip";
import * as reduxSelectors from '../store/application.reducers';
import UserSearch from '../input/UserSearch.component';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from "components/translations/useTranslation";
import Confirm from '../popup/ConfirmActionPopup.component';
import {getDepartments, filterChanged, saveDepartmentChanges} from "./departments.action";

import SearchGroupInput from "../input/SearchGroupInput.component";
import Checkbox from "components/common/checkbox.component";
import {launchDarkly} from "utils/launchDarkly";
import {useModalContext} from "components/modals/ModalContext";
import {HEADING_INFO_TOOLTIP_PLACE, SHARED_TOOLTIP_ID} from "components/tooltip/AppTooltip.component";
import {MODAL_ACTIONS, MODAL_SEVERITY} from "utils/constants";
import {
    Department,
    DepartmentApprover,
    DepartmentsFiltersProps,
    DepartmentsHeaderRowProps,
    DepartmentsManagersViewProps,
    DepartmentsProps
} from "types/departments";

export function Departments(props: DepartmentsProps) {

    const dispatch = useDispatch();
    const {translate} = useTranslation();

    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userRoles = useSelector(reduxSelectors.getUsersRoles);
    const allDepartments: Department[] = useSelector(reduxSelectors.getDepartments);
    const filter: string = useSelector(reduxSelectors.getDepartmentsFilter);
    const possibleManagers: DepartmentApprover[] = useSelector(reduxSelectors.getPossibleDepartmentManagers);

    const useNewModals = launchDarkly.companyClient?.variation("use-new-modals", false);
    const toggleModal = useModalContext()?.toggleModal;

    const [showDeletedDepartments, setShowDeletedDepartments] = useState(false);

    const canUseNewModals = !!useNewModals && !!toggleModal;
    const isNavigationBlocked = !!props.blocker?.isNavigationBlocked();

    // seeded from the first render, so the prompt belongs to a navigation the user started
    // here and not to a page that was already being left when it mounted
    const wasNavigationBlocked = useRef(isNavigationBlocked);

    useEffect(() => {
        dispatch(getDepartments(currentContextId));
    }, [currentContextId]); // eslint-disable-line react-hooks/exhaustive-deps -- reload only when the company changes

    const remain = () => {
        props.blocker?.blocker?.reset?.();
    };

    const leave = () => {
        props.blocker?.blocker?.proceed?.();
    };

    useEffect(() => {
        if (canUseNewModals && isNavigationBlocked && !wasNavigationBlocked.current) {
            toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
                title: translate("popUp.unsavedChangesTitle"),
                description: translate("settings.confirmPageLeave"),
                confirmButtonText: "popUp.stay",
                cancelButtonText: "popUp.leave",
                severity: MODAL_SEVERITY.INFORMATION,
                handleAction: () => {
                    toggleModal?.(null);
                    remain();
                },
                closeCallback: () => leave(),
            });
        }

        wasNavigationBlocked.current = isNavigationBlocked;
    });

    const save = (departmentId: number, values: DepartmentApprover | DepartmentApprover[]) => {
        const departmentManagerIds = (Array.isArray(values) ? values : [values])
            .filter((approver) => approver.value)
            .map((approver) => approver.value);

        dispatch(saveDepartmentChanges({departmentId, departmentManagerIds}, currentContextId));
    };

    const filterDepartments = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(filterChanged(event.target.value));
    };

    const removeFilter = () => {
        dispatch(filterChanged(""));
    };

    const toggleDeletedDepartments = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setShowDeletedDepartments((shown) => !shown);
    };

    const departments = allDepartments
        .filter((department) => department.name && (!department.mdmDeleted || showDeletedDepartments))
        .map((department) => (
            <div key={department.internalOrgUnitId + "-" + department.organizationUnitId}
                 className="row align-items-center py-4">
                <div className={"col-md-4 " + (department.mdmDeleted ? " text-danger" : "")}>
                    {department.name} {"(" + department.organizationUnitId + ")"} {department.mdmDeleted && translate("departments.deleted")}
                </div>
                <div className="col-md-8">
                    <ManagersView managers={department.managers}
                                  orgUnitId={department.internalOrgUnitId}
                                  canEdit={!department.mdmDeleted}
                                  translate={translate}
                                  disabled={!userRoles.systemAdministrator}
                                  valueChanged={save}
                                  options={possibleManagers}/>
                </div>
            </div>
        ));

    // no visible rows means no header row either, just the message inside the panel
    const noData = departments.length === 0;

    return (
        <div>
            {isNavigationBlocked && !canUseNewModals &&
                <Confirm translate={translate}
                         message={translate("settings.confirmPageLeave")}
                         handleAction={remain}
                         closeCallback={leave}/>
            }
            <main className="w-100">
                <main id="departments"
                      className="page w-100 mt-48 overflow-y-auto d-flex justify-content-center align-items-start">
                    <div className="py-16 mb-48 w-75 settings-page__container">
                        <h2 className="ps-4 mb-4 d-flex align-items-center">{translate("departments.header")}
                            <span className="ms-4"
                                  data-for={SHARED_TOOLTIP_ID} data-place={HEADING_INFO_TOOLTIP_PLACE}
                                  data-tip="departments-header-info-tooltip">
                            <CircleQuestionMark size={ICON_SIZE.MEDIUM}/>
                        </span>
                            <Tooltip
                                id="departments-header-info-tooltip"
                                content={() => translate("departments.headerInfo")}
                            />
                        </h2>

                        <div className="bg-white radius-4 p-4 setting_collapsable-main">
                            <Filters translate={translate}
                                     filter={filter}
                                     search={filterDepartments}
                                     clearSearch={removeFilter}
                                     showDeletedDepartments={showDeletedDepartments}
                                     toggleDeletedDepartments={toggleDeletedDepartments}/>

                            {noData ?
                                <div className="actions-list">
                                    <div
                                        className="main-panel radius-4 d-flex align-items-center justify-content-center">
                                        <h3 className="w-100 text-center m-0">
                                            {translate("generic.noData")}
                                        </h3>
                                    </div>
                                </div>
                                :
                                <div className="actions-list">
                                    <HeaderRow translate={translate}/>
                                    <div className="main-panel radius-4">
                                        {departments}
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </main>
            </main>
        </div>
    );
}

const Filters = (props: DepartmentsFiltersProps) => {
    return (
        <div className="d-flex justify-content-end gap-3 align-items-center mb-24">
            {/* the Checkbox label carries w-100, so it needs a shrink-to-fit wrapper to not stretch the row */}
            <div className="d-flex align-items-center text-nowrap">
                <Checkbox
                    inputId={"showDeletedDepartments"}
                    onChange={props.toggleDeletedDepartments}
                    isChecked={props.showDeletedDepartments}
                    ariaLabel={props.translate("departments.showDeleted")}
                    label={props.translate("departments.showDeleted")}
                />
            </div>
            <SearchGroupInput onInputChange={props.search}
                              value={props.filter}
                              className="ga-input__default"
                              onClearInput={props.clearSearch}
                              translate={props.translate}/>


        </div>
    )
};

const HeaderRow = (props: DepartmentsHeaderRowProps) => {
    return (
        <div className="row header-row mx-0">
            <div className="col-md-4">
                {props.translate("departments.departments")}
            </div>
            <div className="col-md-8">
                {props.translate("departments.departmentApprovers")}
            </div>
        </div>
    )
};

const ManagersView = (props: DepartmentsManagersViewProps) => {
    const valueChanged = function (value: DepartmentApprover | DepartmentApprover[]) {
        props.valueChanged(props.orgUnitId, value);
    };
    return (
        <span>
            {props.canEdit &&
                <UserSearch propagateValue={valueChanged}
                            value={props.managers}
                            options={props.options}
                            stopFocus={true}
                            disabled={props.disabled}
                            multi={true}
                            menuPosition={"fixed"}
                            translate={props.translate}/>
            }

        </span>
    )
};

export default Departments;
